/**
 * 运行时面板的纯逻辑：饱和度、冷却倒计时、健康判定。没有 Vue、没有 i18n、没有网络 —— 可单测。
 *
 * 三件事在这里定死，界面只负责画：
 *   1. **饱和度 = max(tpm/max_tpm, rpm/max_rpm)**（产品形态 板块 5）。没设上限的那一维不参与
 *      比较；两维都没上限 → 返回 `null`（**「没有上限」不是「0% 饱和」**，判不出来就如实说出来，
 *      不编一个 0 塞进去）。
 *   2. 冷却剩余秒数**随时间走动**：接口给的是取数那一刻的剩余，界面按经过的时间自己减。
 *   3. 健康状态只有四种，全部由字段直接决定，不掺任何猜测。
 *
 * @typedef {import('../../api/llm/index.js').KeyRuntimeRow} KeyRuntimeRow
 * @typedef {'authFailing'|'cooling'|'saturated'|'healthy'} KeyHealth
 */

import { MODEL_CAPABILITIES } from './model-capability.js'

/** 饱和度到了这个比例就该被一眼看见（快撞上限了）。 */
const SATURATED_AT = 0.9

/**
 * 饱和度：两维取大的那个。两维都没有上限时返回 null。
 * @param {KeyRuntimeRow} row
 * @returns {number|null}
 */
export function saturationOf(row) {
  const hasTpmLimit = row.max_tpm != null
  const hasRpmLimit = row.max_rpm != null
  if (!hasTpmLimit && !hasRpmLimit) return null
  if (!hasRpmLimit) return row.tpm_saturation
  if (!hasTpmLimit) return row.rpm_saturation
  return Math.max(row.tpm_saturation, row.rpm_saturation)
}

/**
 * 冷却剩余：接口给的是 `fetchedAt` 那一刻的秒数，界面按经过的时间减。减到 0 为止。
 * @param {number} remainingAtFetchS  接口给的 `cooldown_remaining_s`
 * @param {number} elapsedMs          从取数到现在过了多少毫秒（必须 >= 0）
 * @returns {number}
 */
export function cooldownRemaining(remainingAtFetchS, elapsedMs) {
  if (elapsedMs < 0) throw new Error(`cooldownRemaining: elapsedMs 不能是负数（${elapsedMs}）`)
  return Math.max(0, remainingAtFetchS - Math.floor(elapsedMs / 1000))
}

/**
 * 这把 key 此刻是什么状态。**顺序即优先级**：还在冷却、而且最近 24h 有认证失败 —— 这就是那把
 * 被吊销的 key（keyrouter 对 auth 失败只打 5 分钟冷却，等 admin 在 DB 关停），它得排在最前面
 * 被认出来。
 * @param {KeyRuntimeRow} row
 * @param {number} cooldownS  当前的冷却剩余（已随时间走动过的那个）
 * @returns {KeyHealth}
 */
export function keyHealth(row, cooldownS) {
  if (cooldownS > 0 && row.auth_failures_24h > 0) return 'authFailing'
  if (cooldownS > 0) return 'cooling'
  const saturation = saturationOf(row)
  if (saturation != null && saturation >= SATURATED_AT) return 'saturated'
  return 'healthy'
}

/** 健康状态 → Badge 的语义色。 @param {KeyHealth} health */
export function healthVariant(health) {
  if (health === 'authFailing') return 'danger'
  if (health === 'cooling') return 'warning'
  if (health === 'saturated') return 'warning'
  if (health === 'healthy') return 'success'
  throw new Error(`healthVariant: 未知的健康状态 ${health}`)
}

/** 不属模型能力、凭据也在密钥池里的服务：数字人、实时音视频与云录制，与服务端 service-key-vendors.js 对应。 */
export const SERVICE_KEY_CAPABILITIES = Object.freeze(['avatar', 'rtc', 'cloud'])

/**
 * key 池的 vendor（`text:volcengine` / `image:seedream` / `tts:doubao` / `avatar:vidu`）→ 能力接入 `{ capability, code }`。
 * 前缀就是能力类型，一条规则、零特判。停用一把 key 要按能力接入读取密钥池中的完整配置行。
 * @param {string} vendor
 * @returns {{ capability: string, code: string }}
 */
export function capabilityFromVendor(vendor) {
  const [capability, code] = vendor.split(':')
  if (!code || (!MODEL_CAPABILITIES.includes(capability) && !SERVICE_KEY_CAPABILITIES.includes(capability))) {
    throw new Error(`capabilityFromVendor: 认不出的 vendor「${vendor}」`)
  }
  return { capability, code }
}

/**
 * 秒 → `4m 32s` / `48s`。0 不进这里（界面上 0 显示成「不在冷却」）。
 * @param {number} seconds
 */
export function formatCooldown(seconds) {
  if (seconds <= 0) throw new Error(`formatCooldown: 只格式化正数秒（收到 ${seconds}）`)
  const minutes = Math.floor(seconds / 60)
  const rest = seconds % 60
  return minutes > 0 ? `${minutes}m ${rest}s` : `${rest}s`
}

/**
 * 比例 → 百分比字符串。`null`（没有上限）不进这里，界面另有文案。
 * @param {number} ratio
 */
export function formatPercent(ratio) {
  return `${(ratio * 100).toFixed(ratio < 0.1 ? 1 : 0)}%`
}

/**
 * 次数 / token 数 / 字节数 → 千分位（版式规范 五：`21,522`）。
 * @param {number} count
 */
export function formatCount(count) {
  return count.toLocaleString('en-US')
}
